import { styled } from "@mui/material/styles";
import { SkeletonComponent, useNotificationStore } from "csi/service/NotificationStore";
import { useEffectAsync } from "designSystem/hooks";
import { Button, CsiImage, FlexInfiniteLoader, FlexRow, FlexRowCenter, FormControlLabel, Placeholder, Popover, Switch, Tab, TabPanel, Tabs, Text } from "designSystem/index";
import { black, grey } from "designSystem/styles/color";
import { POPOVER_BOTTOM_END } from "designSystem/styles/popover-positions";
import { observer } from "mobx-react-lite";
import React, { FunctionComponent, useEffect } from "react";
import PlaceholderImage from "./NotificationPlaceholderEmpty.png";

const PREFIX = "Dropdown";

const classes = {
	tabs: `${PREFIX}tabs`,
	notificationsPane: `${PREFIX}notificationsPane`,
	separator: `${PREFIX}separator`,
	placeholderImage: `${PREFIX}placeholderImage`,
	spreader: `${PREFIX}spreader`,
	spreaderInner: `${PREFIX}spreaderInner`,
	tabContainer: `${PREFIX}tabContainer`,
};

const SwitchLabel = styled(FormControlLabel)({
	marginRight: 12,
	marginLeft: "auto",
});

const LinkButtonRight = styled(Button)({
	float: "right",
	color: grey.A100,
	marginTop: -16,
	marginRight: 16,
});

const StyledPopover = styled(Popover)((
	{
		theme,
	},
) => ({
	"& .MuiPaper-root": {
		top: 30,
		overflow: "hidden",
	},
}));

const Root = styled("div")((
	{
		theme,
	},
) => ({
	paddingTop: 18,
	maxHeight: "inherit",

	[`& .${classes.tabs}`]: {
		color: black[500],
		"& span": {
			fontWeight: "bold",
		},
		margin: 16,
	},
	[`& .${classes.notificationsPane}`]: {
		marginLeft: 16,
		marginTop: 2,
	},
	[`& . ${classes.separator}`]: {
		background: grey[400],
		height: 1,
		marginTop: 16,
		marginRight: -12,
		marginLeft: -12,
	},
	[`& .${classes.placeholderImage}`]: {
		paddingTop: 50,
		paddingBottom: 20,
		width: 312,
	},
	// preserve space for vertical scroller, prevent flicker when scroller appears
	[`& .${classes.spreader}`]: {
		height: 0,
		overflowY: "scroll",
		visibility: "hidden",
		minWidth: 529,
	},
	[`& .${classes.spreaderInner}`]: {
		width: 512,
	},
	[`& .${classes.tabContainer}`]: {
		maxHeight: "calc(100vh - 152px)",
		overflowY: "auto",
		paddingLeft: 16,
		paddingBottom: 12,
	},
}));

// Placeholder component shown when there are no notifications yet
const EmptyPlaceholder = () => {
	const { showAllCaughtUp } = useNotificationStore();

	return (
		<FlexRowCenter
			flexGrow={1}
			justifyContent="center"
			minWidth={496}
			px={10}
		>
			<Placeholder titleProps={{ res: showAllCaughtUp ? "NotificationsNoUnread" : "NoNotification" }}>
				{{ image: (
					<CsiImage
						className={classes.placeholderImage}
						size="medium"
						src={PlaceholderImage}
					/>
				) }}
			</Placeholder>
		</FlexRowCenter>
	);
};

const TaskTabContent: FunctionComponent = observer(() => {
	const {
		tasksContent,
	} = useNotificationStore();

	return (
		<>
			<div className={classes.spreader}>
				<div className={classes.spreaderInner} />
			</div>
			<div className={classes.tabContainer}>
				{tasksContent.length ? tasksContent : <EmptyPlaceholder />}
			</div>
		</>
	);
});

const ConversationsTabContent: FunctionComponent = observer(() => {
	const {
		chatsContent,
		fetchNotifications,
		loadingFirstFetch,
		moreNotificationsToLoad,
		setLoadingFirstFetch,
	} = useNotificationStore();

	useEffectAsync(async () => {
		setLoadingFirstFetch(true);
		await fetchNotifications();
		setLoadingFirstFetch(false);
	}, []);

	return (
		<>
			<div className={classes.spreader}>
				<div className={classes.spreaderInner} />
			</div>
			<div className={classes.tabContainer}>
				{loadingFirstFetch ? (
					SkeletonComponent
				) : chatsContent.length ? (
					<FlexInfiniteLoader
						SkeletonComponent={SkeletonComponent}
						hasMoreResults={moreNotificationsToLoad}
						onMoreResults={fetchNotifications}
					>
						{chatsContent}
					</FlexInfiniteLoader>
				) : (
					<EmptyPlaceholder />
				)}
			</div>
		</>
	);
});

const MessagesTabContent: FunctionComponent = observer(() => {
	const {
		messagesContent,
	} = useNotificationStore();

	return (
		<>
			<div className={classes.spreader}>
				<div className={classes.spreaderInner} />
			</div>
			<div className={classes.tabContainer}>
				{messagesContent.length ? messagesContent : <EmptyPlaceholder />}
			</div>
		</>
	);
});

export const Dropdown: FunctionComponent = observer(() => {
	const {
		elem,
		handleOnClose,
		handleTabsChange,
		labelConversations,
		labelMessages,
		labelNotificationTask,
		markAllReadClick,
		onUnreadChange,
		selectedPanelIndex,
		selectedTabIndex,
		tasksActive,
		title,
		unreadOnlyChecked,
		showMarkAllRead,
	} = useNotificationStore();

	// This fix known react bug with Tabs being redered incorrectly inside popover
	// Fix may be removed after bug fix.
	// Thee are many related react bugs opened and closed, but issue still not fixed
	useEffect(() => {
		setTimeout(() => {
			window.dispatchEvent(new CustomEvent("resize"));
		}, 0);
	}, []);

	return (
		<StyledPopover
			anchorEl={elem}
			onClose={handleOnClose}
			open
			{...POPOVER_BOTTOM_END}
		>
			<Root>
				<FlexRow>
					<Text
						bold
						className={classes.notificationsPane}
						size={16}
						text={title}
					/>
					<SwitchLabel
						control={<Switch checked={unreadOnlyChecked} onChange={onUnreadChange} />}
						labelPlacement="start"
						res="NotificationsUnreadOnly"
					/>
				</FlexRow>
				<Tabs
					className={classes.tabs}
					onChange={handleTabsChange}
					value={selectedTabIndex}
				>
					{tasksActive &&
						<Tab label={labelNotificationTask} />
					}
					<Tab label={labelConversations} />
					<Tab label={labelMessages} />
				</Tabs>
				{showMarkAllRead && (
					<LinkButtonRight
						design="link"
						onClick={markAllReadClick}
						res="NotificationsMarkAllAsRead"
					/>
				)}
				<TabPanel index={0} selectedTabIndex={selectedPanelIndex}>
					<TaskTabContent />
				</TabPanel>
				<TabPanel index={1} selectedTabIndex={selectedPanelIndex}>
					<ConversationsTabContent />
				</TabPanel>
				<TabPanel index={2} selectedTabIndex={selectedPanelIndex}>
					<MessagesTabContent />
				</TabPanel>
			</Root>
		</StyledPopover>
	);
});

export default Dropdown;